import Refresh from "@mui/icons-material/Refresh"
import {
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material"
import { FC, useCallback, useState } from "react"
import { useTranslation } from "react-i18next"
import { Link, useParams } from "react-router-dom"
import {
  ActionReqExecutionModeEnum,
  ActionReqThingTypeMrn,
  ActionReqTypeEnum,
} from "shared/actionReqData"
import { UserThingTypeMrn } from "shared/userData"
import { v4 as uuid } from "uuid"
import { useCreateThingsMutation } from "../features/things/thingsApi"
import { useGetAdministratorByIdQuery } from "../features/things/thingsSaApi"
import AdminTenantsList from "./AdminTenantsList"
import SaLayout from "./SaLayout"
import SaUserEditForm from "./SaUserEditForm"
import { Sl2Btn } from "./Sl2Btn"

export const SaAdministratorPage: FC = () => {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()

  const [isEditing, setIsEditing] = useState(true)
  const [isDeleting, setIsDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false)

  const q = useGetAdministratorByIdQuery({
    id: id!,
  })

  const [createThings] = useCreateThingsMutation()

  const administrator = q.currentData?.administrator
  const isLoading = q.isLoading
  const error = q.error

  const onRefresh = useCallback(() => {
    if (isLoading) {
      return
    }
    q.refetch()
  }, [isLoading, q])

  const deleteUser = useCallback(async () => {
    if (!administrator) return

    try {
      setIsDeleting(true)
      setDeleteError(null)

      const action = {
        thingTypeMrn: ActionReqThingTypeMrn,
        executionMode: ActionReqExecutionModeEnum.sync,
        actionType: ActionReqTypeEnum.DeleteThing,
        idempotencyKey: uuid(),
        tenantId: "noId",
        inputData: {
          id: administrator.id,
          tenantId: "noId",
          thingTypeMrn: UserThingTypeMrn,
        },
      }

      await createThings({
        body: { things: [action] },
      }).unwrap()

      // Handle user deletion - could navigate back to administrators list
      console.log("Administrator deleted:", administrator.id)
    } catch (error: any) {
      setDeleteError(error.message)
    } finally {
      setIsDeleting(false)
    }
  }, [createThings, administrator])

  if (error) {
    return (
      <SaLayout selectedMenuItemLabelKey="administrators">
        <Stack direction="column" spacing={4} p={2} pl={4} mt={4} width="100%">
          <Typography variant="h5" color="error">
            {t("errorLoadingAdministrator")}
          </Typography>
        </Stack>
      </SaLayout>
    )
  }

  if (isLoading || !administrator) {
    return (
      <SaLayout selectedMenuItemLabelKey="administrators">
        <Stack direction="column" spacing={4} p={2} pl={4} mt={4} width="100%">
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="h5">{t("administrator")}</Typography>
            <CircularProgress size={24} />
          </Stack>
        </Stack>
      </SaLayout>
    )
  }

  return (
    <SaLayout selectedMenuItemLabelKey="administrators">
      <Stack direction="column" spacing={4} p={2} pl={4} mt={4} maxWidth={800}>
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
          <Link
            to="/sa/administrators"
            style={{ textDecoration: "none", color: "inherit" }}
          >
            {t("administrators")}
          </Link>
          <Typography color="text.primary">
            {administrator.firstName} {administrator.lastName}
          </Typography>
        </Breadcrumbs>

        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="h5">
            {t("administrator")}: {administrator.firstName}{" "}
            {administrator.lastName}
          </Typography>
          {!isLoading && (
            <IconButton onClick={onRefresh}>
              <Refresh />
            </IconButton>
          )}
          {isLoading && <CircularProgress size={24} />}
          <Sl2Btn variant="outlined" onClick={() => setIsEditing(true)}>
            {t("edit")}
          </Sl2Btn>
        </Stack>

        {isEditing ? (
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t("editAdministrator")}
            </Typography>
            <SaUserEditForm
              user={administrator}
              superAdmin={true}
              onSave={() => {
                setIsEditing(false)
                q.refetch()
              }}
              onError={(error: any) => {
                console.error("Error updating administrator:", error)
              }}
              onCancel={() => setIsEditing(false)}
            />
          </Paper>
        ) : (
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t("generalInfo")}
            </Typography>
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2">{t("firstName")}</Typography>
                <Typography variant="body1">
                  {administrator.firstName}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2">{t("lastName")}</Typography>
                <Typography variant="body1">
                  {administrator.lastName}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2">{t("email")}</Typography>
                <Typography variant="body1">{administrator.email}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2">{t("status")}</Typography>
                <Typography variant="body1">{administrator.status}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2">{t("externalId")}</Typography>
                <Typography variant="body1">
                  {administrator.externalId}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2">{t("isSuperAdmin")}</Typography>
                <Typography variant="body1">
                  {administrator.isSuperAdmin ? t("yes") : t("no")}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2">{t("createdAt")}</Typography>
                <Typography variant="body1">
                  {administrator.createdAt || "-"}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2">{t("updatedAt")}</Typography>
                <Typography variant="body1">
                  {administrator.updatedAt || "-"}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        )}

        <AdminTenantsList administratorId={administrator.id} />

        <Paper
          elevation={2}
          sx={{ p: 3, border: "1px solid", borderColor: "error.main" }}
        >
          <Typography variant="h6" gutterBottom color="error">
            {t("dangerZone")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t("dangerZoneDescription")}
          </Typography>
          <Button
            variant="contained"
            color="error"
            onClick={() => setIsConfirmingDelete(true)}
            disabled={isDeleting}
          >
            {t("deleteAdministrator")}
          </Button>
        </Paper>

        <Dialog data-ui="UI-SL2W-127"
          open={isConfirmingDelete}
          onClose={() => setIsConfirmingDelete(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{t("confirmDelete")}</DialogTitle>
          <DialogContent>
            <Stack direction="column" spacing={1}>
              <DialogContentText>
                {t("confirmDeleteUserWarning")}
              </DialogContentText>
              {deleteError && (
                <DialogContentText color={"error"}>
                  {deleteError}
                </DialogContentText>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setIsConfirmingDelete(false)}
              disabled={isDeleting}
            >
              {t("cancel")}
            </Button>
            <Button
              onClick={async () => {
                await deleteUser()
                setIsConfirmingDelete(false)
              }}
              variant="contained"
              color="error"
              disabled={isDeleting}
            >
              {t("delete")}
            </Button>
          </DialogActions>
        </Dialog>
      </Stack>
    </SaLayout>
  )
}

export default SaAdministratorPage
